import { useEffect, useState, useContext, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router";
import PropTypes from "prop-types";
import { Button } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import {
  AddWorkflow,
  UpdateWorkflow,
  GetWorkflowById,
  GetBadgeColors,
  GetAllCustomReminders,
  GetBaseWorkflowStatuses,
  GetBaseWorkflowActions,
  GetSystemWorkflowActions,
} from "../../../../../services/api/workflow-api";
import {
  errorNotification,
  successNotification,
} from "../../../../../redux/notifications";
import {
  GetCurrentUserNewAPI,
  LoginContext,
} from "../../../../../services/api/authentication-api";
import { v4 as uuidv4, NIL as NIL_UUID } from "uuid";
import DropdownMain from "../../../../../components/dropdown-main/DropdownMain";
import CancelModal from "../../../../../components/cancel-modal/cancel-modal";
import EditWorkflowActionsModal from "./task-actions-section/EditWorkflowActionModal";
import EditWorkflowStatusModal from "./task-status-section/EditWorkflowStatusModal";
import NoPermissionTable from "../../../../../components/no-permission-table/NoPermissionTable";
import WorkflowHeader from "./subcomponents/WorkflowHeader";
import GeneralPropertiesSection from "./subcomponents/GeneralPropertiesSection";
import TaskStatusSection from "./task-status-section/TaskStatusSection";
import TaskActionsSection from "./task-actions-section/TaskActionsSection";
import FooterActions from "./subcomponents/FooterActions";

const BUILTIN_DEFAULT_BADGE = {
  Id: 8,
  Name: "Blue",
  BackgroundColor: "#e4f0ff",
  FontColor: "#004aa4",
  BorderColor: "#004aa4",
};

const CreateEditWorkflow = ({ useLegacyMode, onToggleChange, showLegacyToggle, ...props }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const context = useContext(LoginContext);

  const [editMode] = useState(history.location.pathname.split("/")[3] === "edit");
  const [workflowBasic, setWorkflowBasic] = useState({});
  const [initialStatusList, setInitialStatusList] = useState([]);
  const [initialActionList, setInitialActionList] = useState([]);
  const [workflowStatuses, setWorkflowStatuses] = useState([]); // custom (non-base)
  const [workflowActions, setWorkflowActions] = useState([]); // custom (non-base)
  const [workflowBaseStatuses, setWorkflowBaseStatuses] = useState([]);
  const [workflowBaseActions, setWorkflowBaseActions] = useState([]);
  const [workflowSystemActions, setWorkflowSystemActions] = useState([]);
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [selectedWorkflowStatus, setSelectedWorkflowStatus] = useState([]);
  const [selectedWorkflowAction, setSelectedWorkflowAction] = useState([]);
  const [permission, setPermission] = useState(true);
  const [customReminders, setCustomReminders] = useState([]);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [badgeColorOptions, setBadgeColorOptions] = useState([]);
  const canEdit = context?.currentUser?.privileges["Workflow"]?.Edit || false;
  
  useEffect(() => {
    getPermission();
    getCustomReminders();
    if (editMode) {
      getWorkflowById();
    } else {
      initialStatusListLoad([]);
      initialActionListLoad([]);
      getBaseWorkflowStatuses();
      getBaseWorkflowActions();
      getSystemWorkflowActions();
      getBadgeColors();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPermission = () => {
    GetCurrentUserNewAPI(context?.userID)
      .then((res) => {
        setPermission(res?.User?.Role?.SuperAdmin || res?.User?.Role?.Admin); //REMOVE ADMIN CHECK WHEN ROLLING OUT TO ALL CUSTOMERS
      })
      .catch((err) => {
        console.log("Error [GetUser] => ", err);
        setPermission(false);
      });
  };

  const getCustomReminders = () => {
    GetAllCustomReminders()
      .then((res) => {
        if (res.status === 200) {
          setCustomReminders(res.data);
        }
      })
      .catch((err) => {
        console.log("Error [GetCustomReminders] => ", err);
      });
  };

  const getWorkflowById = () => {
    GetWorkflowById(history.location.pathname.split("/")[4])
      .then((res) => {
        if (res.status === 200) {
          const result = res.data;
          result.InitialStatus = { id: result.InitialStatus, name: result.InitialStatus };
          setWorkflowBasic(result);
          setWorkflowStatuses(result?.StatusListItems?.filter((x) => !x.IsBase));
          setWorkflowBaseStatuses(result?.StatusListItems?.filter((x) => x.IsBase));
          initialStatusListLoad(
            result?.StatusListItems?.filter((x) => x.Active && !x.IsBase).map((x) => ({
              id: x.Name,
              name: x.Name,
              isBase: false,
            }))
          );

          setBadgeColorOptions(result?.BadgeColors || []);

          setWorkflowActions(result?.ActionListItems?.filter((x) => !x.IsBase && !x.IsSystem));
          setWorkflowBaseActions(result?.ActionListItems?.filter((x) => x.IsBase));
          setWorkflowSystemActions(result?.ActionListItems?.filter((x) => x.IsSystem));
          initialActionListLoad(
            result?.ActionListItems?.filter((x) => x.Active && !x.IsBase && !x.IsSystem).map((x) => ({
              id: x.Name,
              name: x.Name,
              isBase: false,
              isSystem: false,
            }))
          );
        }
      })
      .catch((err) => {
        console.log("Error [GetWorkflowById] => ", err);
      });
  };

  const getBadgeColors = () => {
    GetBadgeColors()
      .then((res) => {
        if (res.status === 200) {
          setBadgeColorOptions(res.data || []);
        }
      })
      .catch((err) => {
        console.log("Error [GetBadgeColors] => ", err);
      });
  };

  const getBaseWorkflowStatuses = () => {
    GetBaseWorkflowStatuses()
      .then((res) => {
        if (res.status === 200) {
          const base = res.data;
          const initStat = base?.filter((x) => x.IsInitialStatus).map((x) => ({
            id: x.Name,
            name: x.Name,
            isBase: x.IsBase,
          }));
          if (initStat?.length > 0) {
            setWorkflowBasic((prev) => ({ ...prev, InitialStatus: initStat[0] }));
          }
          setWorkflowBaseStatuses(base);
        }
      })
      .catch((err) => {
        console.log("Error [GetBaseWorkflowStatuses] => ", err);
      });
  };

  const getBaseWorkflowActions = () => {
    GetBaseWorkflowActions()
      .then((res) => {
        if (res.status === 200) {
          const baseActions = res.data;
          setWorkflowBaseActions(baseActions);
        }
      })
      .catch((err) => {
        console.log("Error [GetBaseWorkflowActions] => ", err);
      });
  };

  const getSystemWorkflowActions = () => {
    GetSystemWorkflowActions()
      .then((res) => {
        if (res.status === 200) {
          const systemActions = res.data;
          setWorkflowSystemActions(systemActions);
        }
      })
      .catch((err) => {
        console.log("Error [GetSystemWorkflowActions] => ", err);
      });
  };

  const initialStatusListLoad = (statuses) => {
    let list = [
      { id: "Not Started", name: "Not Started", isBase: true },
      { id: "In Progress", name: "In Progress", isBase: true },
    ];
    list = list.concat(statuses);
    setInitialStatusList(list);
  };

  const initialActionListLoad = (actions) => {
    let list = [
      { id: "Not Started", name: "Not Started", isBase: true, isSystem: false },
      { id: "In Progress", name: "In Progress", isBase: true, isSystem: false },
    ];
    list = list.concat(actions);
    setInitialActionList(list);
  };

  const setStatusFieldName = (id, name) => {
    setWorkflowStatuses((prev) =>
      prev.map((field) => (field.Id === id ? { ...field, Name: name } : field))
    );
  };

  const setActionFieldName = (id, name) => {
    setWorkflowActions((prev) =>
      prev.map((field) => (field.Id === id ? { ...field, Name: name } : field))
    );
  };

  const hideBaseStatus = (id, hidden) => {
    setWorkflowBaseStatuses((prev) =>
      prev.map((field) => (field.Id === id ? { ...field, IsHidden: !hidden } : field))
    );
  };

  const hideBaseAction = (id, hidden) => {
    setWorkflowBaseActions((prev) =>
      prev.map((field) => (field.Id === id ? { ...field, IsHidden: !hidden } : field))
    );
  };

  const hideSystemAction = (id, hidden) => {
    setWorkflowSystemActions((prev) =>
      prev.map((field) => (field.Id === id ? { ...field, IsHidden: !hidden } : field))
    );
  };

  const setAutomaticTriggerForInitialBaseStatus = (name) => {
    setWorkflowBaseStatuses((prev) =>
      prev.map((field) =>
        field.Name === name
          ? { ...field, IsInitialStatus: true }
          : {
              ...field,
              IsInitialStatus: false,
              AutomaticTriggers: field?.AutomaticTriggers?.split(",")
                .filter((t) => t !== "Task Creation")
                .join(","),
            }
      )
    );
    setWorkflowStatuses((prev) =>
      prev.map((field) => ({
        ...field,
        IsInitialStatus: false,
        AutomaticTriggers: field?.AutomaticTriggers?.split(",")
          .filter((t) => t !== "Task Creation")
          .join(","),
      }))
    );
  };

  const setAutomaticTriggerForInitialBaseAction = (name) => {
    setWorkflowBaseActions((prev) =>
      prev.map((field) =>
        field.Name === name
          ? { ...field, IsInitialStatus: true }
          : {
              ...field,
              //IsInitialAction: false,
              AutomaticTriggers: field?.AutomaticTriggers?.split(",")
                .filter((t) => t !== "Task Creation")
                .join(","),
            }
      )
    );
    setWorkflowActions((prev) =>
      prev.map((field) => ({
        ...field,
        IsInitialAction: false,
        AutomaticTriggers: field?.AutomaticTriggers?.split(",")
          .filter((t) => t !== "Task Creation")
          .join(","),
      }))
    );
  };

  const setAutomaticTriggerForInitialStatus = (name) => {
    setWorkflowStatuses((prev) =>
      prev.map((field) =>
        field.Name === name
          ? { ...field, IsInitialStatus: true }
          : {
              ...field,
              IsInitialStatus: false,
              AutomaticTriggers: field?.AutomaticTriggers?.split(",")
                .filter((t) => t !== "Task Creation")
                .join(","),
            }
      )
    );
    setWorkflowBaseStatuses((prev) =>
      prev.map((field) => ({
        ...field,
        IsInitialStatus: false,
        AutomaticTriggers: field?.AutomaticTriggers?.split(",")
          .filter((t) => t !== "Task Creation")
          .join(","),
      }))
    );
  };

  const setAutomaticTriggerForInitialAction = (name) => {
    setWorkflowActions((prev) =>
      prev.map((field) =>
        field.Name === name
          ? { ...field, IsInitialAction: true }
          : {
              ...field,
              IsInitialAction: false,
              AutomaticTriggers: field?.AutomaticTriggers?.split(",")
                .filter((t) => t !== "Task Creation")
                .join(","),
            }
      )
    );
    setWorkflowBaseActions((prev) =>
      prev.map((field) => ({
        ...field,
        //IsInitialAction: false,
        AutomaticTriggers: field?.AutomaticTriggers?.split(",")
          .filter((t) => t !== "Task Creation")
          .join(","),
      }))
    );
  };

  const removeStatusField = (removeIdx) => {
    setWorkflowStatuses((prev) => prev.filter((_, idx) => removeIdx !== idx));
  };

  const removeActionField = (removeCustomIdx) => {
    setWorkflowActions(prev => {
      const customIndexesInPrev = [];
      prev.forEach((x, i) => {
        if (!x.IsHidden && !x.IsBase && !x.IsSystem) customIndexesInPrev.push(i);
      });
      const removeIdx = customIndexesInPrev[removeCustomIdx];
      if (removeIdx === undefined) return prev;
      return prev.filter((_, i) => i !== removeIdx);
    });
  };

  const removeInitialStatus = (name) => {
    setInitialStatusList((prev) => prev.filter((x) => name !== x.name));
  };

  const removeInitialAction = (name) => {
    setInitialActionList((prev) => prev.filter((x) => name !== x.name));
  };

  const addNewWorkflowStatus = () => {
    const obj = {
      Id: uuidv4(),
      Name: workflowStatuses?.length === 0 ? "New Task Status" : `New Task Status - ${workflowStatuses?.length}`,
      Statuses: [],
      StatusListItems: [],
      ActionListItems: [],
      Active: true,
      //IsBase: false,
      BadgeColorId: BUILTIN_DEFAULT_BADGE.Id,
      BadgeColor: BUILTIN_DEFAULT_BADGE,
    };
    setWorkflowStatuses((prev) => [...prev, obj]);
    setInitialStatusList((prev) => [...prev, { id: obj.Name, name: obj.Name, isBase: false }]);
  };

  const addNewWorkflowAction = () => {
    const obj = {
      Id: uuidv4(),
      Name: workflowActions?.length === 0 ? "New Task Action" : `New Task Action - ${workflowActions?.length}`,
      Statuses: [],
      StatusListItems: [],
      ActionListItems: [],
      MaxInstances: 0,
      AvailableForStatus: NIL_UUID,
      HideForStatus: NIL_UUID,
      Active: true,
      DueDateExtensionDefault: 0,
      DueDateExtendProject: null,
      ExtendByDaysMax: 0,
    };
    setWorkflowActions((prev) => [...prev, obj]);
    setInitialActionList((prev) => [...prev, { id: obj.Name, name: obj.Name, isBase: false, isSystem: false }]);
  };

  const handleStatusSwap = (firstIdx, secondIdx) => {
    // Keep existing animations
    const a = document.getElementById(`workflowstatus_${firstIdx}`);
    const b = document.getElementById(`workflowstatus_${secondIdx}`);
    if (a && b) {
      a.classList.remove("moveUp", "moveDown");
      b.classList.remove("moveUp", "moveDown");
      void a.offsetWidth; // reflow
      void b.offsetWidth;
      a.classList.add("moveDown");
      b.classList.add("moveUp");
    }

    const [first, second] = [workflowStatuses[firstIdx], workflowStatuses[secondIdx]];
    setWorkflowStatuses((prev) =>
      prev.map((f, idx) => (idx === firstIdx ? second : idx === secondIdx ? first : f))
    );
  };

  const handleActionSwap = (fromCustomIdx, toCustomIdx) => {
    // animate current (custom) rows before state change
    const a = document.getElementById(`workflowaction_${fromCustomIdx}`);
    const b = document.getElementById(`workflowaction_${toCustomIdx}`);
    if (a && b) {
      a.classList.remove("moveUp", "moveDown");
      b.classList.remove("moveUp", "moveDown");
      void a.offsetWidth; // reflow
      void b.offsetWidth;
      a.classList.add("moveDown");
      b.classList.add("moveUp");
    }

    setWorkflowActions(prev => {
      // Build mapping of visible custom actions -> indices in the full array
      const customIndexesInPrev = [];
      for (let i = 0; i < prev.length; i++) {
        const x = prev[i];
        if (!x.IsHidden && !x.IsBase && !x.IsSystem) customIndexesInPrev.push(i);
      }

      // Bounds check against the *custom* list
      if (
        fromCustomIdx < 0 || toCustomIdx < 0 ||
        fromCustomIdx >= customIndexesInPrev.length ||
        toCustomIdx >= customIndexesInPrev.length
      ) {
        return prev; // nothing to do
      }

      // Translate to indices in the backing array
      const i = customIndexesInPrev[fromCustomIdx];
      const j = customIndexesInPrev[toCustomIdx];

      // Swap immutably in the backing array
      const next = prev.slice();
      const tmp = next[i];
      next[i] = next[j];
      next[j] = tmp;
      return next;
    });
  };

  const onInitialStatusSelect = useCallback(
    (selectedValue) => {
      setWorkflowBasic((prev) => ({ ...prev, InitialStatus: selectedValue }));
      if (selectedValue?.isBase) {
        setAutomaticTriggerForInitialBaseStatus(selectedValue?.name);
      } else {
        setAutomaticTriggerForInitialStatus(selectedValue?.name);
      }
    },
    []
  );

  // const onInitialActionSelect = useCallback(
  //   (selectedValue) => {
  //     setWorkflowBasic((prev) => ({ ...prev, InitialAction: selectedValue }));
  //     if (selectedValue?.isBase) {
  //       setAutomaticTriggerForInitialBaseAction(selectedValue?.name);
  //     } else {
  //       setAutomaticTriggerForInitialAction(selectedValue?.name);
  //     }
  //   },
  //   []
  // );

  //de-dupe helper
  const uniqById = (arr) => {
    const seen = new Set();
    return arr.filter(x => {
      const key = String(x?.Id);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  // --- Save -----------------------------------------------------------------
  const saveWorkflow = () => {
    if (!editMode) {
      // Create
      const workflowDto = {
        Id: uuidv4(),
        Name: workflowBasic?.Name,
        Legacy: false,
        Description: workflowBasic?.Description,
        InitialStatus: workflowBasic?.InitialStatus?.name,
        StatusListItems: workflowBaseStatuses?.concat(workflowStatuses),
        ActionListItems: uniqById([
          ...workflowBaseActions,
          ...workflowActions,
          ...workflowSystemActions,
        ]),
      };
      if (!workflowBasic?.Name) {
        dispatch(
          errorNotification({ header: "Workflow Creation Failed", message: "Please Enter Workflow Name." })
        );
      } else if (!workflowBasic?.InitialStatus) {
        dispatch(
          errorNotification({ header: "Workflow Creation Failed", message: "Please Select Initial Status." })
        );
      } else {
        AddWorkflow(workflowDto)
          .then((res) => {
            if (res.status === 200) {
              history.push("/admin?tab=workflows");
              dispatch(
                successNotification({ header: "Workflow Created", message: `${workflowBasic?.Name} Created Successfully.` })
              );
            }
          })
          .catch((err) => {
            console.log("Cannot Save Workflow Settings : CreateWorkflow : ", err);
            dispatch(
              errorNotification({ header: "Workflow Creation Failed", message: err?.response?.data?.message })
            );
          });
      }
    } else {
      // Edit
      const workflowDto = {
        Id: workflowBasic.Id,
        Name: workflowBasic.Name,
        Legacy: false,
        Description: workflowBasic.Description,
        InitialStatus: workflowBasic.InitialStatus?.name,
        StatusListItems: workflowBaseStatuses?.concat(workflowStatuses),
        ActionListItems: uniqById([
          ...workflowBaseActions,
          ...workflowActions,
          ...workflowSystemActions,
        ]),
      };
      UpdateWorkflow(workflowDto)
        .then((res) => {
          if (res.status === 200) {
            history.push("/admin?tab=workflows");
            dispatch(
              successNotification({ header: "Workflow Updated", message: `${workflowBasic?.Name} Updated Successfully.` })
            );
          } else {
            dispatch(
              errorNotification({ header: "Workflow Update Failed", message: "Unable to update workflow. Error Code :" + res.status })
            );
          }
        })
        .catch((err) => {
          console.log("Cannot Save Workflow Settings : Update : ", err);
          dispatch(
            errorNotification({ header: "Workflow Creation Failed", message: err?.response?.data?.message })
          );
        });
    }
  };

  if (!permission) return <NoPermissionTable />;

  return (
    <div className="admin-workflow-page-wrapper">
      <Helmet>
        <meta charSet="utf-8" />
        <title>{editMode ? `TRIYO · ${workflowBasic?.Name}` : "TRIYO · Create Workflow"}</title>
      </Helmet>

      <WorkflowHeader 
        editMode={editMode} 
        name={workflowBasic?.Name}
        useLegacyMode={useLegacyMode}
        onToggleChange={onToggleChange}
        showLegacyToggle={showLegacyToggle}
      />

      <div className="workflow-creation-panel">
        {/* GENERAL PROPERTIES */}
        <GeneralPropertiesSection
          workflowBasic={workflowBasic}
          setWorkflowBasic={setWorkflowBasic}
          initialStatusList={initialStatusList}
          onInitialStatusSelect={onInitialStatusSelect}
        />

        <hr className="divider" />

        {/* TASK STATUS */}
        <TaskStatusSection
          workflowBaseStatuses={workflowBaseStatuses}
          workflowStatuses={workflowStatuses}
          setSelectedWorkflowStatus={setSelectedWorkflowStatus}
          setIsStatusModalOpen={setIsStatusModalOpen}
          hideBaseStatus={hideBaseStatus}
          setFieldName={setStatusFieldName}
          handleStatusSwap={handleStatusSwap}
          removeField={removeStatusField}
          removeInitialStatus={removeInitialStatus}
          addNewWorkflowStatus={addNewWorkflowStatus}
        />

        <hr className="divider" />

        {/* TASK ACTIONS */}
        <TaskActionsSection
          workflowBaseActions={workflowBaseActions}
          workflowSystemActions={workflowSystemActions}
          workflowActions={workflowActions}
          setSelectedWorkflowAction={setSelectedWorkflowAction}
          setIsActionModalOpen={setIsActionModalOpen}
          hideBaseAction={hideBaseAction}
          hideSystemAction={hideSystemAction}
          setFieldName={setActionFieldName}
          handleActionSwap={handleActionSwap}
          removeField={removeActionField}
          removeInitialAction={removeInitialAction}
          addNewWorkflowAction={addNewWorkflowAction}
        />

        <hr className="divider" />

        <FooterActions
          onCancel={() => setShowCancelModal(true)}
          onSave={saveWorkflow}
          isEditMode={editMode}
          canEdit={canEdit}
        />
      </div>

      {/* Workflow Status Edit Modal */}
      <EditWorkflowStatusModal
        isStatusModalOpen={isStatusModalOpen}
        setIsStatusModalOpen={setIsStatusModalOpen}
        openWorkflowStatus={selectedWorkflowStatus}
        workflowStatuses={workflowStatuses}
        setWorkflowStatuses={setWorkflowStatuses}
        workflowBaseStatuses={workflowBaseStatuses}
        setWorkflowBaseStatuses={setWorkflowBaseStatuses}
        workflowBasic={workflowBasic}
        setWorkflowBasic={setWorkflowBasic}
        badgeColorOptions={badgeColorOptions}
      />

      {/* Workflow Actions Edit Modal */}
      <EditWorkflowActionsModal
        isActionModalOpen={isActionModalOpen}
        setIsActionModalOpen={setIsActionModalOpen}
        openWorkflowAction={selectedWorkflowAction}
        workflowStatuses={workflowStatuses}
        workflowBaseStatuses={workflowBaseStatuses}
        workflowActions={workflowActions}
        setWorkflowActions={setWorkflowActions}
        workflowBaseActions={workflowBaseActions}
        setWorkflowBaseActions={setWorkflowBaseActions}
        workflowSystemActions={workflowSystemActions}
        setWorkflowSystemActions={setWorkflowSystemActions}
        customReminders={customReminders}
        workflowBasic={workflowBasic}
        setWorkflowBasic={setWorkflowBasic}
      />

      { showCancelModal &&
        <CancelModal
          show={showCancelModal}
          onHide={() => setShowCancelModal(false)}
          redirectURL={"/admin?tab=workflows"}
        />
      }
    </div>
  );
};

CreateEditWorkflow.propTypes = {
  id: PropTypes.number,
  useLegacyMode: PropTypes.bool,
  onToggleChange: PropTypes.func,
  showLegacyToggle: PropTypes.bool,
};

export default CreateEditWorkflow;
